import { useMemo } from 'react'

interface LocationMixedChartProps {
  labels: string[]
  values: number[]
  onBarClick?: (label: string) => void
  className?: string
  height?: number
  maxItems?: number
  valueLabel?: string
  showHeader?: boolean
  /** underline = orange under-bar (detail); row = muted full-row bar (Overview) */
  variant?: 'underline' | 'row'
}

export default function LocationMixedChart({
  labels = [],
  values = [],
  onBarClick,
  className = '',
  maxItems = 7,
  valueLabel = 'Visitors',
  showHeader = true,
  variant = 'underline',
}: LocationMixedChartProps) {
  const chartData = useMemo(() => {
    if (!labels.length || !values.length) return []

    const totalVisitors = values.reduce((acc, val) => acc + val, 0)

    const combinedData = labels.map((label, index) => {
      const value = values[index] || 0
      const percentage = totalVisitors > 0 ? ((value / totalVisitors) * 100).toFixed(1) : '0'
      
      return {
        location: label,
        visitors: value,
        fullLabel: label,
        percentage
      }
    })

    return combinedData
      .sort((a, b) => b.visitors - a.visitors)
      .slice(0, maxItems)
  }, [labels, values, maxItems])

  const handleBarClick = (data: any) => {
    if (onBarClick && data?.fullLabel) {
      onBarClick(data.fullLabel)
    }
  }

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-xs text-muted-foreground">
        No location data available
      </div>
    )
  }

  const maxVal = Math.max(...chartData.map(d => d.visitors))

  return (
    <div className={`w-full flex flex-col gap-2 ${className}`}>
      {/* Column Headers */}
      {showHeader && (
        <div className="flex items-center justify-between text-xs font-medium text-muted-foreground/60 px-2 pb-1 border-b border-border/20">
          <span>Location</span>
          <div className="flex items-center gap-3">
            <span>{valueLabel}</span>
            <span className="w-12 text-right">Share</span>
          </div>
        </div>
      )}
      
      {chartData.map((item, index) => {
        const shareWidth = `${(item.visitors / maxVal) * 100}%`

        if (variant === 'row') {
          return (
            <div
              key={index}
              className="group relative flex items-center justify-between gap-3 cursor-pointer px-2 py-2 -mx-2 rounded-sm transition-colors duration-150 overflow-hidden"
              onClick={() => handleBarClick(item)}
            >
              <div
                className="absolute inset-y-0 left-0 rounded-sm bg-muted/40 dark:bg-muted/50 group-hover:bg-muted/55 dark:group-hover:bg-muted/70 transition-colors"
                style={{ width: shareWidth }}
              />
              <span className="relative z-10 text-xs font-medium text-foreground truncate max-w-[65%]">
                {item.location}
              </span>
              <div className="relative z-10 flex items-center gap-3 shrink-0">
                <span className="text-xs text-foreground font-medium tabular-nums">
                  {item.visitors.toLocaleString()}
                </span>
                <span className="text-xs text-muted-foreground w-12 text-right tabular-nums">
                  {item.percentage}%
                </span>
              </div>
            </div>
          )
        }

        return (
          <div 
            key={index} 
            className="group flex flex-col gap-1.5 cursor-pointer hover:bg-muted/30 px-2 py-1.5 -mx-2 rounded-md transition-colors duration-150"
            onClick={() => handleBarClick(item)}
          >
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-foreground truncate max-w-[65%]">
                {item.location}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-foreground font-semibold tabular-nums">
                  {item.visitors.toLocaleString()}
                </span>
                <span className="text-muted-foreground w-12 text-right tabular-nums">
                  {item.percentage}%
                </span>
              </div>
            </div>
            
            <div className="h-1 w-full bg-muted/40 rounded-full overflow-hidden group-hover:bg-muted/60 transition-colors duration-150">
              <div 
                className="h-full bg-[var(--chart-1)] rounded-full transition-all duration-500 ease-out group-hover:brightness-110"
                style={{ width: shareWidth }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}
